/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect } from "react";

import { isDev } from "@/lib/chillnn/enviroment";
import { payjpDelete, payjpInit } from "@/lib/payjp";
import type { Payjp } from "@/lib/payjp/type";

/**
 *
 * @description フォームのplaceholderおよびエラー時のメッセージの言語を指定します。デフォルトはja(日本語)で、他にen(英語)を指定できます。
 *
 */
type Locale = "ja" | "en";

/**
 *
 * @type {string}
 * card - カード番号入力欄（ブランドアイコン付）、有効期限入力欄、CVC入力欄の順に横並んだフォーム
 * cardNumber - カード番号入力フォーム。自動フォーマット、カードブランド画像表示機能をもつ
 * cardExpiry - 有効期限入力フォーム。‘月 / 年’で自動フォーマット
 * cardCvc - CVC入力フォーム
 *
 */
type ElementType = "card" | "cardNumber" | "cardExpiry" | "cardCvc";

/**
 *
 * @type {object}
 * @member style - フォームに対して独自のスタイルを適用。@see {@link https://pay.jp/docs/payjs#style} style オブジェクトを参照
 * @member placeholder - type = card 以外でのみ適用。フォームに指定されたplaceholderを適用
 * @member disabled - true ならフォームのinputタグに disabled をつける。デフォルトは false
 * @member hideIcon - type = card のみ適用。 true ならカードブランドのアイコン表示機能をオフにする。デフォルトは false
 *
 */
type ElementOptions = {
	style?: object;
	placeholder?: string;
	disabled?: boolean;
	hideIcon?: boolean;
};

// Payjp のインスタンスを保持する
const payjp = payjpInit("ja");

/**
 *
 * @see https://pay.jp/docs/payjs
 */
export const usePayjpClient = () => {
	useEffect(() => {
		return () => {
			payjpDelete();
		};
	}, []);

	/**
	 *
	 * Elements インスタンスを生成します。
	 * Elements とは Element インスタンスを束ねる単位です。 Elements インスタンス1つにつき、1組のカード情報入力フォームを用意できます。 ページ内に複数のフォームを作りたい場合は、その分だけ Elements インスタンスを生成ください。
	 * 注. 現状、複数の Elements インスタンスを用意した場合でも、全てのインスタンスに対して同一のlocaleオプションが適用されます。 各フォーム毎に言語を分けることはできませんが、言語の切り替えは、elements.update()で実現可能です。
	 *
	 * @param locale - @see {@link Locale}
	 *
	 * @example
	 * ```
	 * type = Locale: 'ja' | 'en';
	 * const locale: type = 'ja';
	 * const jaElements = payjp.elements({ locale });
	 *
	 * const locale: type = 'en';
	 * const enElements = payjp.elements({ locale });
	 *
	 * const elements = payjp.elements();
	 * ```
	 *
	 */
	const createPayjpElements = useCallback((locale: Locale) => {
		try {
			return payjp?.elements({ locale }) as unknown as Payjp.Elements;
		} catch (error) {
			if (isDev) {
				// eslint-disable-next-line no-console
				console.error(error);
			}
		}
	}, []);

	/**
	 *
	 * card を生成した場合、その elements から他のtypeを生成することはできません。
	 * cardNumber を生成した場合、その elements から更に cardExpiry などを生成して組み合わせます（ card は生成できません）。 入力フォームを縦に並べるなど、より自由度の高い配置が可能です。
	 *
	 * @param elements - Elements インスタンス(createPayjpElements で生成したもの)
	 * @param type - @see {@link ElementType}
	 * @param options - @see {@link ElementOptions}
	 *
	 * @example
	 * ```
	 * const cardElement = elements.create('card', {style: {base: {color: '#fff'}}});
	 * ```
	 *
	 */
	const createPayjpElement = useCallback((elements: any, type: ElementType, options?: ElementOptions) => {
		try {
			return elements.create(type, options) as unknown as Payjp.Element;
		} catch (error) {
			if (isDev) {
				// eslint-disable-next-line no-console
				console.error(error);
			}
		}
	}, []);

	/**
	 *
	 * @param element - @see {@link createPayjpElement} createPayjpElement で生成したもの
	 *
	 * @returns {Promise<{ id: string; error: { message: string } } | undefined>} - 生成に成功した場合、id が返り、エラーが発生した場合、error.message が返る。予期せぬエラーが発生した場合、undefined が返る。
	 *
	 * @example
	 * ```
	 * const elements = createPayjpElements({ locale: 'ja' });
	 * const cardNumberElement = createPayjpElement(elements, 'cardNumber', { style: { base: { color: '#fff' } } });
	 * const result = await createPayjpToken(cardNumberElement);
	 * ```
	 */
	const createPayjpToken = useCallback(async (element: HTMLElement, cardHolderName: string, cardEmail: string) => {
		try {
			const result = await payjp?.createToken(element, {
				three_d_secure: true,
				card: {
					name: cardHolderName,
					email: cardEmail,
				},
			});
			return result;
		} catch (error) {
			if (isDev) {
				// eslint-disable-next-line no-console
				console.error(error);
			}
		}
	}, []);

	return {
		payjp,
		createPayjpElements,
		createPayjpElement,
		createPayjpToken,
	};
};
